import React, { useEffect, useState } from 'react';
import ProductPageHeaderPreviewThumbnail from './ProductPageHeaderPreviewThumbnail';
import ProductPageHeaderInfo from './ProductPageHeaderInfo';
import DescriptionRenderer from './DescriptionRenderer';
import { Product } from '../data/interfaces/Product';
import { Variation } from '../data/interfaces/Variation';
import { useDarkMode } from '../contexts/DarkModeContext';
import previewMetadata from '../data/previewMetadata.json';
import { API_BASE_URL } from '../data/objects/gameColor';

interface ReleaseHeaderProps {
    product: Product;
    variations: Variation[];
    selectedVariation: Variation;
    handleVariationChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

interface PreviewDescription {
    thumbnailUrl: string;
    description: { type: 'text' | 'link'; content: string; href?: string }[];
}

const typedPreviewMetadata: PreviewDescription[] = previewMetadata as PreviewDescription[];

const ProductPageHeader: React.FC<ReleaseHeaderProps> = ({
    product,
    variations,
    selectedVariation,
    handleVariationChange,
}) => {
    const [previewMedia, setPreviewMedia] = useState<string[]>([]);
    const [selectedPreview, setSelectedPreview] = useState<string | null>(null);
    const [selectedDescription, setSelectedDescription] = useState<PreviewDescription | null>(null);
    const { isDarkMode } = useDarkMode();

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

    useEffect(() => {
        const previewPath = `/assets/releases/${selectedVariation.releaseId}/variations/v${selectedVariation.variationId}/previews`;

        fetch(`${API_BASE_URL}/api/list-previews?path=${previewPath}`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Error fetching media previews: ${response.statusText}`);
                }
                return response.json();
            })
            .then((data) => {
                const allPreviews = [selectedVariation.thumbnailUrl, ...(data.mediaFiles || [])];
                setPreviewMedia(allPreviews);
                setSelectedPreview(allPreviews[0]);
            })
            .catch((error) => console.error('Error fetching preview media:', error));
    }, [selectedVariation]);

    useEffect(() => {
        if (previewMedia.length > 0) {
            setSelectedPreview(previewMedia[0]);
        }
    }, [previewMedia]);

    useEffect(() => {
        const matchingMetadata = typedPreviewMetadata.find(
            (meta) => meta.thumbnailUrl.replace(/\\/g, '/') === selectedPreview?.replace(/\\/g, '/')
        );
        setSelectedDescription(matchingMetadata || null);
    }, [selectedPreview]);

    const handleThumbnailClick = (imageUrl: string) => {
        window.scrollTo({ top: 0 });
        setSelectedPreview(imageUrl);
    };

    const isVideo = (file: string) => /\.(mp4|webm|avi|mov)$/i.test(file);

    // Open modal when clicking on an image, but not on video
    const openModal = () => {
        if (!selectedPreview || isVideo(selectedPreview)) return;
        const index = previewMedia.findIndex((url) => url === selectedPreview);
        setCurrentMediaIndex(index);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
    };

    const showNextMedia = () => {
        setCurrentMediaIndex((prevIndex) => (prevIndex + 1) % previewMedia.length);
    };

    const showPreviousMedia = () => {
        setCurrentMediaIndex((prevIndex) => (prevIndex - 1 + previewMedia.length) % previewMedia.length);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'ArrowRight') {
            showNextMedia();
        } else if (e.key === 'ArrowLeft') {
            showPreviousMedia();
        } else if (e.key === 'Escape') {
            closeModal();
        }
    };

    useEffect(() => {
        if (isModalOpen) {
            window.addEventListener('keydown', handleKeyDown);
            return () => window.removeEventListener('keydown', handleKeyDown);
        }
    }, [isModalOpen]);

    return (
        <div className={`release-header card mb-3 ${isDarkMode ? 'dark-mode-2' : 'light-mode-2'}`}>
            <div className="row g-0">
                {/* Left column: Main preview */}
                <div className="col-lg-7 main-preview">
                    {selectedPreview ? (
                        isVideo(selectedPreview) ? (
                            <video
                                src={selectedPreview}
                                controls
                                className="img-fluid rounded"
                                style={{ maxHeight: '400px', width: '100%' }}
                            />
                        ) : (
                            <img
                                src={selectedPreview}
                                className="img-fluid rounded"
                                alt={selectedVariation.title}
                                onClick={openModal}
                                style={{ cursor: 'pointer' }}
                            />
                        )
                    ) : (
                        <img
                            src={selectedVariation.thumbnailUrl}
                            className="img-fluid rounded"
                            alt={selectedVariation.title}
                        />
                    )}

                    {/* Description for the selected preview */}
                    {selectedDescription && (
                        <div className={`card row release-info-container ${isDarkMode ? 'dark-mode-3' : 'light-mode-3'} my-2`}>
                            <DescriptionRenderer description={selectedDescription.description} />
                        </div>
                    )}

                    {/* Preview Thumbnails */}
                    <ProductPageHeaderPreviewThumbnail
                        previewMedia={previewMedia}
                        onThumbnailClick={handleThumbnailClick}
                    />
                </div>

                {/* Right column: Information */}
                <div className="col-lg-5">
                    <ProductPageHeaderInfo
                        product={product}
                        variations={variations}
                        selectedVariation={selectedVariation}
                        handleVariationChange={handleVariationChange}
                    />
                </div>
            </div>

            {/* Full-screen modal for preview images */}
            {isModalOpen && (
                <div>
                    <button className="modal-arrow left-arrow" onClick={showPreviousMedia}>&#10094;</button>
                    <div className="custom-modal-overlay" onClick={closeModal}>
                        <div className="custom-modal show" onClick={(e) => e.stopPropagation()}>
                            <div
                                className={`modal-media-container ${isDarkMode ? 'dark-mode-gallery-modal' : 'light-mode-2'}`}
                            >
                                {isVideo(previewMedia[currentMediaIndex]) ? (
                                    <video
                                        src={previewMedia[currentMediaIndex]}
                                        controls
                                        className="custom-modal-video-media"
                                        autoPlay
                                    />
                                ) : (
                                    <img
                                        src={previewMedia[currentMediaIndex]}
                                        alt={`Preview ${currentMediaIndex + 1}`}
                                        className="custom-modal-image-media rounded"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(previewMedia[currentMediaIndex], '_blank');
                                        }}
                                    />
                                )}
                            </div>

                            {/* Media counter */}
                            <div className="media-counter">
                                {`${currentMediaIndex + 1}/${previewMedia.length}`}
                            </div>
                        </div>
                    </div>
                    <button className="modal-arrow right-arrow" onClick={showNextMedia}>&#10095;</button>
                    <button className="close" onClick={closeModal}>&times;</button>
                </div>
            )}
        </div>
    );
};

export default ProductPageHeader;

